import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Textarea, Label } from '@/components/ui'
import { Hash, Copy, Check, FileText } from 'lucide-react'
import { toast } from 'sonner'

interface HashResult {
  algorithm: string
  hash: string
  copied: boolean
}

export function HashGenerator() {
  const [input, setInput] = useState('')
  const [hashes, setHashes] = useState<HashResult[]>([])
  const [loading, setLoading] = useState(false)

  const algorithms = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512']

  const generateHashes = async (text: string) => {
    if (!text) {
      setHashes([])
      return
    }

    setLoading(true)
    const encoder = new TextEncoder()
    const data = encoder.encode(text)

    const results: HashResult[] = []

    for (const algo of algorithms) {
      try {
        const hashBuffer = await crypto.subtle.digest(algo, data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
        results.push({ algorithm: algo, hash: hashHex, copied: false })
      } catch {
        results.push({ algorithm: algo, hash: 'Error generating hash', copied: false })
      }
    }

    // Add MD5-like hash (simple simulation - not real MD5)
    let simpleHash = 0
    for (let i = 0; i < text.length; i++) {
      simpleHash = ((simpleHash << 5) - simpleHash) + text.charCodeAt(i)
      simpleHash = simpleHash & simpleHash
    }
    const md5Like = Math.abs(simpleHash).toString(16).padStart(32, '0').slice(0, 32)
    results.unshift({ algorithm: 'MD5 (simulated)', hash: md5Like, copied: false })

    setHashes(results)
    setLoading(false)
  }

  useEffect(() => {
    const debounce = setTimeout(() => {
      generateHashes(input)
    }, 300)

    return () => clearTimeout(debounce)
  }, [input])

  const copyHash = async (index: number) => {
    const hash = hashes[index]
    if (!hash) return

    try {
      await navigator.clipboard.writeText(hash.hash)
      setHashes((prev) =>
        prev.map((h, i) => ({
          ...h,
          copied: i === index,
        }))
      )
      toast.success(`${hash.algorithm} hash copied`)
      setTimeout(() => {
        setHashes((prev) =>
          prev.map((h) => ({ ...h, copied: false }))
        )
      }, 2000)
    } catch {
      toast.error('Failed to copy hash')
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      setInput(text)
      toast.success(`File loaded: ${file.name}`)
    } catch {
      toast.error('Failed to read file')
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Hash className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Hash Generator</h1>
            <p className="text-muted-foreground">Generate cryptographic hashes for text and files</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Enter text or upload a file to hash</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="input">Text Input</Label>
              <Textarea
                id="input"
                placeholder="Enter text to hash..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="min-h-[200px] font-mono"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label
                htmlFor="file-upload"
                className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-accent transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>Upload File</span>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </Label>

              <Button
                variant="outline"
                onClick={() => setInput('')}
                disabled={!input}
              >
                Clear
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              {input.length} characters • {new Blob([input]).size} bytes
            </p>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Hashes</CardTitle>
            <CardDescription>Generated cryptographic hashes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : hashes.length > 0 ? (
              <div className="space-y-3">
                {hashes.map((hash, index) => (
                  <div
                    key={hash.algorithm}
                    className="p-3 rounded-lg bg-muted/30 border border-border"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-muted-foreground">
                        {hash.algorithm}
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyHash(index)}
                        className="h-6 px-2"
                      >
                        {hash.copied ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="font-mono text-xs break-all text-foreground">
                      {hash.hash}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Hash className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Enter text to generate hashes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
              <Hash className="h-4 w-4 text-primary" />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About Hash Functions</p>
              <p>
                Cryptographic hash functions generate fixed-size outputs from variable-size inputs.
                SHA-256 and SHA-512 are recommended for security applications.
                Note: MD5 shown here is simulated for demonstration purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
